import * as Yup from "yup";

const phoneRegExp = /^[987][0-9]{9}$/;
const pincodeRegExp = /^[1-9][0-9]{5}$/;
const aadhaarRegExp = /^\d{12}$/;
const panRegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const panRegExpIndividual = /^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$/;
const panRegExpOrganisation = /^[A-Z]{3}[CAFJLTGHB][A-Z][0-9]{4}[A-Z]$/;
const gstinRegExp = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const verhoeff = {
  d: [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
  ],
  p: [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
  ],
  inv: [0, 4, 3, 2, 1, 5, 6, 7, 8, 9],

  validate(number) {
    if (!/^\d{12}$/.test(number)) return false;
    let c = 0;
    let invertedArray = number.split("").reverse().map(Number);
    for (let i = 0; i < invertedArray.length; i++) {
      c = this.d[c][this.p[i % 8][invertedArray[i]]];
    }
    return c === 0;
  }
};

const vendorValidationSchema = Yup.lazy((values) => {
  const isIndividual = values?.vendorType === "individual";
  const isOrg = values?.vendorType === "organisation";

  return Yup.object().shape({
    uniqueVendorCode: Yup.string().required("Unique vendor code is required"),
    vendorName: Yup.string().trim().test('not-empty', 'Vendor name cannot be empty', val => !!val && val.trim().length > 0).required("Vendor name is required"),
    phoneNumber: Yup.string()
      .matches(/^[6-9][0-9]{9}$/, 'Invalid phone number format. Must be 10 digits starting with 6-9')
      .required('Phone number is required'),

    whatsappNumber: Yup.string()
    .trim()
      .matches(/^[6-9][0-9]{9}$/, 'Invalid WhatsApp number format. Must be 10 digits starting with 6-9')
      .nullable(),

    vendorEmail: Yup.string()
      .transform((value) => value?.toLowerCase().trim())
      .matches(emailRegExp, "Invalid email format (e.g. user@domain.com)")
      .required("Email is required"),

    vendorType: Yup.string()
      .oneOf(["individual", "organisation"])
      .required("Vendor type is required"),

    // organisationType: isOrg
    //   ? Yup.string()
    //     .oneOf([
    //       "Private Limited",
    //       "Public Limited",
    //       "Sole Proprietorship",
    //       "Partnership",
    //     ])
    //     .required("Organisation type is required")
    //   : Yup.string().nullable(),

    // Individual fields
    aadhaarNumberIndividual: isIndividual
      ? Yup.string()
        .matches(aadhaarRegExp, "Enter valid 12-digit Aadhaar")
        .test(
          "verhoeff-check",
          "Invalid Aadhaar number",
          value => !value || verhoeff.validate(value)
        )
        .required("Aadhaar is required")
      : Yup.string().nullable(),

    panNumberIndividual: isIndividual
      ? Yup.string()
        .matches(panRegExpIndividual, "Invalid PAN format")
        .required("PAN is required")
      : Yup.string().nullable(),

    // Individual GST fields (optional)
    tradeNameIndividual: Yup.string().nullable(),
    gstinIndividual: Yup.string()
      .matches(gstinRegExp, "Invalid GSTIN format")
      .nullable(),
    gstStateIndividual: Yup.string().nullable(),

    // Org fields
    companyName: isOrg
      ? Yup.string().trim().test('not-empty', 'Company name cannot be empty', val => !!val && val.trim().length > 0).required("Company name is required")
      : Yup.string().nullable(),

    companyPanNumber: isOrg
      ? Yup.string()
        .matches(panRegExpOrganisation, "Invalid PAN format")
        .required("Company PAN is required")
      : Yup.string().nullable(),
    
    // Organisation GST fields (optional)
    tradeNameOrganisation: Yup.string().nullable(),
    gstin: Yup.string().matches(gstinRegExp, "Invalid GSTIN format").nullable(),
    gstState: Yup.string().nullable(),
    
    // Owner KYC fields
    ownerAadhaarNumber: isOrg
      ? Yup.string()
        .matches(aadhaarRegExp, "Enter valid 12-digit Aadhaar")
        .test(
          "verhoeff-check",
          "Invalid Aadhaar number",
          value => !value || verhoeff.validate(value)
        )
        .required("Owner Aadhaar is required")
      : Yup.string().nullable(),

    ownerPanNumber: isOrg
      ? Yup.string()
        .matches(panRegExpIndividual, "Invalid PAN format for owner")
        .required("Owner PAN is required")
      : Yup.string().nullable(),

    organisationAddress: isOrg
      ? Yup.string().trim().test('not-empty', 'Address cannot be empty', val => !!val && val.trim().length > 0).required("Address is required")
      : Yup.string().nullable(),

    organisationCity: isOrg
      ? Yup.string().trim().test('not-empty', 'City cannot be empty', val => !!val && val.trim().length > 0).required("City is required")
      : Yup.string().nullable(),

    organisationState: isOrg
      ? Yup.string().trim().test('not-empty', 'State cannot be empty', val => !!val && val.trim().length > 0).required("State is required")
      : Yup.string().nullable(),

    organisationPincode: isOrg
      ? Yup.string()
        .matches(pincodeRegExp, "Invalid pincode")
        .required("Pincode is required")
      : Yup.string().nullable(),

    organisationCountry: isOrg
      ? Yup.string().trim().test('not-empty', 'Country cannot be empty', val => !!val && val.trim().length > 0).required("Country is required")
      : Yup.string().nullable(),

    vendorContacts: isOrg
      ? Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().trim().test('not-empty', 'Contact name cannot be empty', val => !!val && val.trim().length > 0).required("Contact name is required"),
            mobile: Yup.string()
              .matches(phoneRegExp, "Invalid number")
              .required("Contact number is required"),
            email: Yup.string()
              .email("Invalid email")
              .required("Email is required"),
          })
        )
        .min(1, "At least one contact is required")
      : Yup.array().notRequired(),

    // vendorRating: Yup.number().required("Rating is required").min(1).max(5),
    // vendorPrioritisation: Yup.number()
    //   .required("Prioritisation is required")
    //   .min(1)
    //   .max(4),
    vendorStatus: Yup.string()
      .oneOf(["active", "inactive", "suspended", "under_review"], "Invalid status")
      .required("Status is required"),

    additionalDetails: Yup.string().nullable(),
    
    // Document uploads
    aadhaarCardFront: Yup.string().nullable(),
    aadhaarCardBack: Yup.string().nullable(),
    panCardFront: Yup.string().nullable(),
    companyPanCardFront: Yup.string().nullable(),
    ownerPanCardFront: Yup.string().nullable(),
    gstCertificate: Yup.string().nullable(),
  });
});

export default vendorValidationSchema;
